import React from "react";
import { DeleteOutlined, ScissorOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { Badge, Button, Popconfirm, Table, Typography } from "antd";
import dayjs from "dayjs";

import {
  MediaLibraryResourceType,
  VideoUploadStatus,
  type MediaLibraryItem,
} from "@fitness-app/data-models/entities/MediaLibrary";
import { PermissionKey } from "@fitness-app/data-models";

import VideoUploadList from "~/components/VideoUpload/VideoUploadList";
import { usePermission } from "~/hooks/usePermission";
import { statusMapper } from "~/modules/MediaLibrary/MediaLibrary";
import { useDeleteStudioSource, useProduceClips, useStudioRequests, useStudioSources } from "../hooks/useStudioData";

import { isStudioRequestRunning, latestRequestsBySource } from "../hooks/studio-requests.logic";

const uploadStatusLabels: Record<VideoUploadStatus, string> = {
  [VideoUploadStatus.Pending]: "Oczekuje na wysyłkę",
  [VideoUploadStatus.Queued]: "W kolejce",
  [VideoUploadStatus.Processing]: "Przetwarzanie",
  [VideoUploadStatus.Encoding]: "Kodowanie",
  [VideoUploadStatus.Ready]: "Gotowe",
  [VideoUploadStatus.Failed]: "Błąd",
};

/**
 * Surowiec Klipowni: upload długich nagrań (ta sama ścieżka tus → Bunny co biblioteka
 * mediów, osobny resourceType, żeby nie zaśmiecać biblioteki ćwiczeń — TRE-1982) i start
 * pipeline'u per nagranie. Postęp przebiegu czytany z ai_agent_request (polling w hooku).
 */
/** Webinary i długie live'y potrafią ważyć ~10 GB — domyślne 4 GB biblioteki mediów to za mało. */
const STUDIO_SOURCE_LIMIT_MB = 12_000;

export const SourcesPanel = (): React.ReactElement => {
  const sourcesQuery = useStudioSources();
  const requestsQuery = useStudioRequests();
  const produceClips = useProduceClips();
  const deleteSource = useDeleteStudioSource();
  const canUseAgents = usePermission(PermissionKey.AGENTS_USE);

  const requestBySource = latestRequestsBySource(requestsQuery.data ?? []);

  return (
    <div className="flex flex-col gap-4">
      <VideoUploadList
        multiple
        height={100}
        limitInMB={STUDIO_SOURCE_LIMIT_MB}
        type={MediaLibraryResourceType.StudioSource}
        onRemoved={(mediaId) => deleteSource.mutate(mediaId)}
      />

      <Table
        dataSource={sourcesQuery.data ?? []}
        loading={sourcesQuery.isLoading}
        rowKey="id"
        size="small"
        locale={{ emptyText: "Dodaj pierwsze długie nagranie — webinar, live albo sesję Q&A." }}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      >
        <Table.Column<MediaLibraryItem>
          title="Nagranie"
          dataIndex="title"
          render={(title: string) => (
            <span className="flex items-center gap-2">
              <VideoCameraOutlined /> {title}
            </span>
          )}
        />
        <Table.Column<MediaLibraryItem>
          title="Status wideo"
          render={(_, row) => (
            <Badge
              status={statusMapper[row.data.uploadStatus]?.badge ?? "default"}
              text={uploadStatusLabels[row.data.uploadStatus] ?? row.data.uploadStatus}
            />
          )}
        />
        <Table.Column<MediaLibraryItem>
          title="Dodano"
          dataIndex="createdAt"
          width={120}
          render={(createdAt: string | null) => (createdAt ? dayjs(createdAt).format("DD.MM.YYYY") : "—")}
        />
        <Table.Column<MediaLibraryItem>
          title="Produkcja"
          width={280}
          render={(_, row) => {
            const request = requestBySource.get(row.id);
            const isRunning = isStudioRequestRunning(request);

            // An upload that never went through has nothing to produce from: let marketing
            // remove it instead of staring at "Oczekuje na wysyłkę" forever (TRE-2108).
            if (
              row.data.uploadStatus === VideoUploadStatus.Pending ||
              row.data.uploadStatus === VideoUploadStatus.Failed
            ) {
              return (
                <Popconfirm
                  title="Usunąć to nagranie?"
                  description="Wysyłka nie doszła do skutku. Nagranie zniknie z listy; plik trzeba będzie wgrać ponownie."
                  okText="Usuń"
                  okButtonProps={{ danger: true }}
                  cancelText="Anuluj"
                  onConfirm={() => deleteSource.mutate(row.id)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={deleteSource.isLoading && deleteSource.variables === row.id}
                  >
                    Usuń
                  </Button>
                </Popconfirm>
              );
            }

            if (!canUseAgents) {
              return <Typography.Text type="secondary">Brak uprawnienia agents.use</Typography.Text>;
            }

            return (
              <div className="flex items-center gap-2">
                <Popconfirm
                  title="Rozpocząć produkcję klipów?"
                  okText="Rozpocznij"
                  cancelText="Anuluj"
                  disabled={isRunning || row.data.uploadStatus !== VideoUploadStatus.Ready}
                  description={
                    <div className="max-w-xs">
                      Transkrypcja, wybór fragmentów, montaż z napisami i kontrola jakości — wszystko w tle. Koszt
                      zależy od długości nagrania, a gotowe klipy pojawią się na liście poniżej.
                    </div>
                  }
                  onConfirm={() => produceClips.mutate({ sourceMediaId: row.id })}
                >
                  <Button
                    size="small"
                    icon={<ScissorOutlined />}
                    loading={isRunning || produceClips.isLoading}
                    disabled={row.data.uploadStatus !== VideoUploadStatus.Ready}
                  >
                    {isRunning ? "Produkcja w toku..." : "Utwórz klipy"}
                  </Button>
                </Popconfirm>
                {request && !isRunning && request.status !== "SCHEDULED" && (
                  <Typography.Text type={request.status === "FAILED" ? "danger" : "secondary"} className="text-xs">
                    {request.status === "FAILED"
                      ? `Błąd: ${request.data?.error ?? "szczegóły w zadaniach w tle"}`
                      : `Ostatni przebieg: ${request.data?.readyClips ?? 0}/${request.taskCount} klipów`}
                  </Typography.Text>
                )}
              </div>
            );
          }}
        />
      </Table>
    </div>
  );
};
